import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { brl, data, dataHora } from "@/lib/format";
import {
  PageHeader,
  Panel,
  EmptyState,
  StatusBadge,
  Quorum,
  Carregando,
  Erro,
} from "@/components/parker";
import { useEmpresas, useCategorias, useLancamentos, useAprovacoes, useIndice } from "@/lib/dados";
import { registrarAprovacao } from "@/lib/fns";
import { useSessao } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/aprovacoes")({ component: Aprovacoes });

const NA_FILA = ["rascunho", "aguardando_aprovacao"];

function Aprovacoes() {
  const { podeAprovar, usuario } = useSessao();
  const empresas = useEmpresas();
  const categorias = useCategorias();
  const lancamentos = useLancamentos();
  const aprovacoes = useAprovacoes();

  const nomeEmpresa = useIndice(empresas.dados);
  const nomeCategoria = useIndice(categorias.dados);
  const [ocupado, setOcupado] = useState<string | null>(null);
  // Caixa de decisão aberta: qual item e com que intenção.
  const [decidindo, setDecidindo] = useState<{
    id: string;
    decisao: "aprovado" | "rejeitado";
  } | null>(null);
  const [comentario, setComentario] = useState("");

  if (lancamentos.erro) return <Erro mensagem={lancamentos.erro} />;

  const fila = lancamentos.dados
    .filter((l) => NA_FILA.includes(l.status))
    .sort((a, b) => Math.abs(b.valorCentavos) - Math.abs(a.valorCentavos));

  const validas = aprovacoes.dados.filter((a) => !a.invalidada);

  /** Aprovadores distintos que já assinaram — quórum conta pessoa, não clique. */
  const feitas = (lancamentoId: string) =>
    new Set(
      validas
        .filter((a) => a.lancamentoId === lancamentoId && a.decisao === "aprovado")
        .map((a) => a.userId),
    ).size;

  const jaAssinei = (lancamentoId: string) =>
    validas.some((a) => a.lancamentoId === lancamentoId && a.userId === usuario?.uid);

  async function decidir(id: string, decisao: "aprovado" | "rejeitado") {
    // Rejeitar sem motivo devolve o item para a fila sem informação nenhuma
    // para quem o criou. O servidor também exige — aqui é só avisar antes.
    if (decisao === "rejeitado" && comentario.trim().length === 0) {
      toast.error("Explique o motivo da rejeição.");
      return;
    }
    setOcupado(id);
    try {
      const r = await registrarAprovacao({
        lancamentoId: id,
        decisao,
        comentario: comentario.trim() || undefined,
      });
      toast.success(
        r.status === "aprovado"
          ? "Quórum completo — lançamento aprovado."
          : decisao === "rejeitado"
            ? "Lançamento rejeitado."
            : "Sua aprovação foi registrada. Ainda faltam assinaturas.",
      );
      setDecidindo(null);
      setComentario("");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }

  function abrir(id: string, decisao: "aprovado" | "rejeitado") {
    setDecidindo({ id, decisao });
    setComentario("");
  }

  return (
    <>
      <PageHeader
        title="Aprovações"
        subtitle="Cada aprovação é individual, identificada e datada. Aprovação parcial mantém o item na fila."
      />

      {!podeAprovar ? (
        <Panel className="mb-4 p-4 text-sm text-muted-foreground">
          Seu papel atual não aprova lançamentos. Você pode acompanhar a fila.
        </Panel>
      ) : null}

      {lancamentos.carregando ? (
        <Panel>
          <Carregando linhas={4} />
        </Panel>
      ) : (
        <div className="space-y-3">
          {fila.map((l) => {
            const assinadas = feitas(l.id);
            const meuVoto = jaAssinei(l.id);
            const souAutor = l.criadoPor === usuario?.uid;

            return (
              <Panel key={l.id} className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold">{l.descricao}</h3>
                      <StatusBadge status={l.status} />
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {data(l.dataCompetencia)} · paga {nomeEmpresa.get(l.empresaPagadoraId) ?? "—"}{" "}
                      · beneficia {nomeEmpresa.get(l.empresaBeneficiadaId) ?? "—"} ·{" "}
                      {nomeCategoria.get(l.categoriaId) ?? "—"}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="num text-lg font-semibold">{brl(l.valorCentavos)}</div>
                    <div className="label-caps">
                      {assinadas} de {l.aprovacoesNecessarias} aprovações
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <Quorum feitas={assinadas} necessarias={l.aprovacoesNecessarias} />
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {validas
                      .filter((a) => a.lancamentoId === l.id)
                      .map((a) => (
                        <span key={a.id} className="rounded bg-surface-2 px-2 py-1">
                          {a.userNome} · {a.decisao} · {dataHora(a.createdAt)}
                          {a.comentario ? ` · "${a.comentario}"` : ""}
                        </span>
                      ))}
                  </div>

                  {podeAprovar ? (
                    <div className="flex items-center gap-2">
                      {meuVoto ? (
                        <span className="text-xs text-muted-foreground">
                          Você já registrou sua decisão.
                        </span>
                      ) : souAutor ? (
                        <span className="text-xs text-muted-foreground">
                          Você registrou este lançamento — outro aprovador precisa assinar.
                        </span>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            disabled={ocupado === l.id}
                            onClick={() => abrir(l.id, "aprovado")}
                          >
                            Aprovar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={ocupado === l.id}
                            onClick={() => abrir(l.id, "rejeitado")}
                          >
                            Rejeitar
                          </Button>
                        </>
                      )}
                    </div>
                  ) : null}
                </div>

                {decidindo?.id === l.id ? (
                  <div className="mt-3 border-t border-hairline pt-3">
                    <Textarea
                      rows={2}
                      autoFocus
                      value={comentario}
                      onChange={(e) => setComentario(e.target.value)}
                      placeholder={
                        decidindo.decisao === "rejeitado"
                          ? "Por que está rejeitando? (obrigatório)"
                          : "Comentário (opcional)"
                      }
                    />
                    <div className="mt-2 flex gap-2">
                      <Button
                        size="sm"
                        variant={decidindo.decisao === "rejeitado" ? "destructive" : "default"}
                        disabled={ocupado === l.id}
                        onClick={() => void decidir(l.id, decidindo.decisao)}
                      >
                        {decidindo.decisao === "rejeitado"
                          ? "Confirmar rejeição"
                          : "Confirmar aprovação"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDecidindo(null)}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : null}
              </Panel>
            );
          })}

          {fila.length === 0 ? (
            <Panel>
              <EmptyState title="Fila vazia" hint="Nenhum item aguardando aprovação." />
            </Panel>
          ) : null}
        </div>
      )}
    </>
  );
}
